import type { DrawState } from "@classroom/domain";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "./i18n";

export default function DrawResetMode({
  value,
  disabled,
  onChange,
}: {
  value: DrawState["mode"];
  disabled: boolean;
  onChange: (mode: DrawState["mode"]) => void;
}) {
  const { t } = useTranslation("drawHistory");
  const id = useId();
  const root = useRef<HTMLSpanElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const options = [
    { value: "daily", label: t("dailyMode") },
    { value: "untilComplete", label: t("completeMode") },
  ] as const;
  const selected = options.find((option) => option.value === value);
  const activeOption = options[active];

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);
  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [open]);

  function expand() {
    if (disabled) return;
    setActive(
      Math.max(
        0,
        options.findIndex((option) => option.value === value),
      ),
    );
    setOpen(true);
  }
  function select(mode: DrawState["mode"]) {
    if (disabled) return;
    setOpen(false);
    trigger.current?.focus();
    if (mode !== value) onChange(mode);
  }

  return (
    <div className="draw-reset-mode">
      <span>{t("resetMode")}</span>
      <span
        className="class-switcher draw-mode-switcher"
        ref={root}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
        }}
      >
        <button
          type="button"
          role="combobox"
          className="class-switcher-trigger"
          ref={trigger}
          disabled={disabled}
          aria-label={t("resetMode")}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={open ? `${id}-list` : undefined}
          aria-activedescendant={open && activeOption ? `${id}-${activeOption.value}` : undefined}
          onClick={() => (open ? setOpen(false) : expand())}
          onKeyDown={(event) => {
            if (event.key === "Escape" || event.key === "Tab") {
              setOpen(false);
              if (event.key === "Escape" && open) event.preventDefault();
              return;
            }
            if (!["ArrowDown", "ArrowUp", "Home", "End", "Enter", " "].includes(event.key)) return;
            event.preventDefault();
            if (!open) return expand();
            if (event.key === "Enter" || event.key === " ") {
              if (activeOption) select(activeOption.value);
            } else if (event.key === "Home") setActive(0);
            else if (event.key === "End") setActive(options.length - 1);
            else setActive(Math.max(0, Math.min(options.length - 1, active + (event.key === "ArrowDown" ? 1 : -1))));
          }}
        >
          <span className="class-switcher-label">{selected?.label}</span>
          <svg aria-hidden="true" viewBox="0 0 16 16" width="16" height="16" className="class-switcher-chevron">
            <path
              d="m4 6 4 4 4-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        {open && (
          <span id={`${id}-list`} className="class-switcher-menu" role="listbox" aria-label={t("resetMode")}>
            {options.map((option, index) => (
              <button
                type="button"
                role="option"
                id={`${id}-${option.value}`}
                key={option.value}
                className={`class-switcher-option${active === index ? " is-highlighted" : ""}`}
                tabIndex={-1}
                aria-label={option.label}
                aria-selected={option.value === value}
                onMouseDown={(event) => event.preventDefault()}
                onMouseMove={() => setActive(index)}
                onClick={() => select(option.value)}
              >
                <span className="class-switcher-check" aria-hidden="true">
                  {option.value === value ? "✓" : ""}
                </span>
                <span className="class-switcher-option-name">{option.label}</span>
              </button>
            ))}
          </span>
        )}
      </span>
    </div>
  );
}
